import _ from 'lodash';
import React from 'react';

import {
  DataCacheActivityQueryVariables, QueryCachedOrdersFilterInput, StoreTypeName, useDataCacheActivityLazyQuery, useToggleDataCacheExternalOrderArchivedMutation, ValidationState
} from 'generated/graphql';
import {
  errorUtil,
  listQueryUtil
} from 'iron-forge-react-modules';

import { DateTime } from 'luxon';
import * as types from './types';



const useListQuery = listQueryUtil.genUseListQuery<types.IColumnId>();



export interface IExternalOrder {
  id: string;
  orderNumber: string;
  createdAt: DateTime;
  rawData: string;
  passesValidation: boolean;
  validationErrors: string[];
}

export interface IData {
  externalOrders: IExternalOrder[];
}

export function useQuery(validationState?: string, storeId?: string | null) {
  const useWrapQuery = __genWrapQuery(storeId, validationState);
  return useListQuery({
    useLazyQuery: useWrapQuery,
    clientColumn2GqlSortField: {
      metaCreated: 'createdDate'
    },
    initQuerySort: {
      columnId: 'metaCreated',
      direction: 'descending'
    },
    defaultFilterArgs: {
      orderNumber: ''
    },
    genFilter2GqlFilter: () => _.partial(__filter2GqlFilter, storeId),
    gqlResultData2GqlPageInfo: gqlTuple => gqlTuple?.cachedOrders?.pageInfo,
    gqlResultData2TupleData: (gqlTuple): IData => ({
      externalOrders: _.map(gqlTuple.data?.cachedOrders?.nodes ?? [], (node): IExternalOrder => ({
        id: node.id,
        orderNumber: __getOrderNumber(node.rawData, node.store.type.name),
        createdAt: DateTime.fromISO(node.metaCreated),
        rawData: JSON.stringify(node.rawData, null, '  '),
        passesValidation: _.isEmpty(node.validationErrors),
        validationErrors: node.validationErrors
      }))
    })
  });
}

export function useToggleExternalOrderArchived() {
  const [fnMutate, tuple] = useToggleDataCacheExternalOrderArchivedMutation({
    refetchQueries: [
      'DataCacheActivity'
    ]
  });

  const fnWrappedMutate = React.useCallback(
    async (orderId: string) => {
      await fnMutate({
        variables: {
          input: {
            externalOrderId: orderId
          }
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}


function __genWrapQuery(storeId?: string | null, validationState?: string) {
  function useWrappedQuery() {
    const [fnQuery, tuple] = errorUtil.useWrapLazyQueryOrMutationTuple(
      useDataCacheActivityLazyQuery({
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: true
      }));

    const fnWrappedQuery = React.useCallback(
      (params: {
        variables: Pick<DataCacheActivityQueryVariables, 'page' | 'filter'>;
      }) => {
        fnQuery({
          variables: {
            page: params.variables.page,
            filter: {
              ...params.variables.filter,
              storeId,
              inValidationState: [__validationStateStringAsGqlValidationState(validationState)]
            }
          }
        });
      },
      [fnQuery]
    );

    return [
      fnWrappedQuery,
      tuple
    ] as const;
  }

  return useWrappedQuery;
}



function __getOrderNumber(rawData: any, storeType: StoreTypeName) {
  if (storeType === 'wooCommerce' || storeType === 'wooCommerce_buyWithAmazon') {
    return rawData.number;
  } else if (storeType === 'shopify' || storeType === 'shopify_ecommerce') {
    return rawData.name;
  } else if (storeType === 'telgoo5') {
    return rawData['message']['sales-order-submission']['header']['order-header']['customer-order-number'];
  }

  return 'NOT SUPPORTED';
}



function __filter2GqlFilter(
  storeId?: string | null,
  filter?: types.IFilter
): QueryCachedOrdersFilterInput {
  const args = {
    ...(storeId && { storeId }),
    orderNumber: filter?.orderNumber
  };
  return args;
}



function __validationStateStringAsGqlValidationState(validationState?: string): ValidationState {
  if (validationState === 'valid') {
    return 'valid';
  } else if (validationState === 'invalid') {
    return 'invalid';
  } else if (validationState === 'notValidated') {
    return 'notValidated';
  } else if (validationState === 'invalidArchived') {
    return 'invalidArchived';
  }

  return 'valid';
}

